import { Injectable } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
  CollectionReference,
  Query,
} from "@angular/fire/firestore";
import { User } from "../@core/data/users";
import { UserService } from "../@core/services/user.service";

import * as firebase from "firebase/app";
import "firebase/firestore";
import { environment } from "../../environments/environment";
import { HttpClient } from "@angular/common/http";
import WorkingTask from "../models/working-task.model";
import * as moment from "moment";
import { NotificationService } from "./notifications.service";

@Injectable({
  providedIn: "root",
})
export class WorkingTaskService {
  visibility = environment.visibility;
  baseUrl = environment.apiUrl;
  userLogin: User;
  private dbPath = "/shops";
  contentRef: AngularFirestoreCollection<WorkingTask>;

  constructor(
    private http: HttpClient,
    private db: AngularFirestore,
    private userStore: UserService,
    private notiService: NotificationService
  ) {
    this.userStore.getCurrentUser().subscribe((user: User) => {
      if (user) {
        this.userLogin = user;
        this.visibility = user.companyId
          ? user.companyId
          : environment.visibility;
      }
    });
  }

  getWorkingTasks(shopId: string): AngularFirestoreCollection<WorkingTask> {
    return this.db.collection(this.dbPath + `/${shopId}/working-tasks`, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .orderBy("issueDate", "desc")
    );
  }

  getWorkingTasksByRole(shopId: string, fromDate: Date, toDate: Date) {
    var db = firebase.default.firestore();
    var ref;
    if (this.userLogin.moderator) {
      ref = db
        .collection(this.dbPath + `/${shopId}/working-tasks`)
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("issueDate", ">=", fromDate)
        .where("issueDate", "<=", toDate)
        .orderBy("issueDate", "desc");
    } else {
      ref = db
        .collection(this.dbPath + `/${shopId}/working-tasks`)
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("assignBy", "==", this.userLogin.uid)
        .where("issueDate", ">=", fromDate)
        .where("issueDate", "<=", toDate)
        .orderBy("issueDate", "desc");
    }

    return ref.get();
  }

  getWorkingTasksByCustomer(
    shopId: string,
    customerId: string,
    fromDate: Date,
    toDate: Date
  ) {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath + `/${shopId}/working-tasks`)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true)
      .where("customerId", "==", customerId)
      .where("issueDate", ">=", fromDate)
      .where("issueDate", "<=", toDate)
      .orderBy("issueDate", "desc");

    return ref.get();
  }

  getWorkingTasksByIssueDate(
    shopId: string,
    type?: string,
    issueDate?: Date
  ): AngularFirestoreCollection<WorkingTask> {
    return this.db.collection(
      this.dbPath + `/${shopId}/working-tasks`,
      (ref) => {
        type = type ? type : "day";
        issueDate = issueDate ? issueDate : new Date();

        let query: CollectionReference | Query = ref;

        let year = issueDate.getFullYear();
        let month = issueDate.getMonth() + 1;
        let day = issueDate.getDate();
        console.log(`getWorkingTasksByIssueDate: ${day}/${month}/${year}`);

        query = query.where("isDeleted", "==", false);
        query = query.where("isActive", "==", true);
        switch (type) {
          case "year":
            query = query.where("issueDateYear", "==", year);
            break;
          case "month":
            query = query.where("issueDateYear", "==", year);
            query = query.where("issueDateMonth", "==", month);
            break;
          default:
            query = query.where("issueDateYear", "==", year);
            query = query.where("issueDateMonth", "==", month);
            query = query.where("issueDateDay", "==", day);
            break;
        }

        query = query.orderBy("issueDate", "desc");

        return query;
      }
    );
  }

  getWorkingTasksByDateRange(
    shopId: string,
    isActive: boolean = true,
    fromDate: Date,
    toDate: Date
  ): AngularFirestoreCollection<WorkingTask> {
    return this.db.collection(this.dbPath + `/${shopId}/working-tasks`, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", isActive)
        .where("issueDate", ">=", fromDate)
        .where("issueDate", "<=", toDate)
        .orderBy("issueDate", "desc")
    );
  }

  getWorkingTasksByFinished(
    shopId: string,
    isFinished: boolean,
    fromDate: Date,
    toDate: Date
  ): AngularFirestoreCollection<WorkingTask> {
    return this.db.collection(this.dbPath + `/${shopId}/working-tasks`, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("isFinished", "==", isFinished)
        .where("issueDate", ">=", fromDate)
        .where("issueDate", "<=", toDate)
        .orderBy("issueDate", "desc")
    );
  }

  get(id: string) {
    return this.contentRef.doc(id).valueChanges();
  }

  getById(shopId: string, id: string) {
    var db = firebase.default.firestore();
    var ref = db.collection(this.dbPath + `/${shopId}/working-tasks`).doc(id);

    return ref.get();
  }

  create(content: WorkingTask): any {
    content.issueDate = content.issueDate
      ? content.issueDate
      : moment().startOf("day").toDate();
    content.dueDate = content.dueDate
      ? content.dueDate
      : moment(content.issueDate).endOf("day").toDate();
    content.tags = content.tags ? content.tags : [];
    content.privacy = content.privacy ? content.privacy : false;
    content.status = content.status ? content.status : 0;
    content.imagesUrl = content.imagesUrl ? content.imagesUrl : [];
    content.videoUrl = content.videoUrl ? content.videoUrl : "";
    content.content = content.content ? content.content : "";
    content.description = content.description ? content.description : "<p></p>";

    if (!content.assignBy) {
      content.assignBy = this.userLogin.uid;
      content.assignee = this.userLogin;
      content.assignName = this.userLogin.displayName;
    }

    content.noLiked = 0;
    content.noComment = 0;

    //Moderator
    content.isReviewed = false;
    content.isApproved =
      this.userLogin.admin || this.userLogin.moderator ? true : false;
    content.approvedOn = new Date();
    content.approvedBy = this.userLogin.uid;
    content.approvedAuthor = this.userLogin;

    content.isActive = true;
    content.isDeleted = false;
    content.createdOn = new Date();
    content.createdBy = this.userLogin.uid;
    content.updatedOn = new Date();
    content.updatedBy = this.userLogin.uid;
    content.updatedName = this.userLogin.displayName;
    content.author = this.userLogin;

    content.timestamp = new Date().getTime();
    content.visibility = this.visibility;

    content.shopId = content.shopId ? content.shopId : this.userLogin.shopId;

    content.receipt = content.receipt
      ? JSON.parse(JSON.stringify(content.receipt))
      : null;
    content.processors = content.processors
      ? JSON.parse(JSON.stringify(content.processors))
      : [];
    content.strProcessors = content.processors
      ? JSON.stringify(content.processors)
      : "";

    console.log("before create task:", content);

    this.contentRef = this.db.collection(
      this.dbPath + `/${content.shopId}/working-tasks`
    );
    return this.contentRef.add({ ...content }).then((docRef) => {
      content.id = docRef.id;
      if (content.assignBy != this.userLogin.uid) {
        this.notiService.create(
          "Công việc",
          "working-tasks",
          docRef.id,
          docRef.id,
          `${content.updatedName} đã tạo công việc cho bạn`,
          content.title,
          content.assignBy,
          `/working-tasks/${content.id}`,
          ""
        );
      }

      const syncData = { id: content.id, ...content };
      this.sync(syncData);

      console.log("after create task:", content);
      return content;
    });
  }

  update(content: WorkingTask): Promise<void> {
    this.contentRef = this.db.collection(
      this.dbPath + `/${content.shopId}/working-tasks`
    );

    content.issueDate = content.issueDate
      ? content.issueDate
      : moment().startOf("day").toDate();
    content.dueDate = content.dueDate
      ? content.dueDate
      : moment(content.issueDate).endOf("day").toDate();

    return (
      this.contentRef
        .doc(content.id)
        .update({
          title: content.title,
          description: content.description ? content.description : "<p></p>",
          coverUrl: content.coverUrl ? content.coverUrl : "",
          type: content.type ? content.type : "",
          issueDate: content.issueDate,
          issueDateSeconds: content.issueDateSeconds,
          issueDateMinutes: content.issueDateMinutes,
          issueDateHours: content.issueDateHours,
          issueDateDay: content.issueDateDay,
          issueDateWeekDay: content.issueDateWeekDay,
          issueDateMonth: content.issueDateMonth,
          issueDateYear: content.issueDateYear,
          dueDate: content.dueDate,
          sourceType: content.sourceType,
          content: content.content ? content.content : "",
          videoUrl: content.videoUrl ? content.videoUrl : "",
          imagesUrl: content.imagesUrl ? content.imagesUrl : [],
          tags: content.tags ? content.tags : [],
          privacy: content.privacy ? content.privacy : false,
          status: content.status ? content.status : 0,
          priority: content.priority ? content.priority : 0,
          level: content.level ? content.level : 0,
          points: content.points ? content.points : 0,
          isFinished:
            content.status === 4 || content.status === 5 ? true : false,
          finishedOn: new Date(),
          todoDate: content.todoDate ? content.todoDate : new Date(),
          progressDate: content.progressDate
            ? content.progressDate
            : new Date(),
          reviewDate: content.reviewDate ? content.reviewDate : new Date(),
          rejectDate: content.rejectDate ? content.rejectDate : new Date(),
          doneDate: content.doneDate ? content.doneDate : new Date(),

          objectType: content.objectType ? content.objectType : "",
          customerId: content.customerId ? content.customerId : "",
          customerName: content.customerName ? content.customerName : "",
          supplierId: content.supplierId ? content.supplierId : "",
          supplierName: content.supplierName ? content.supplierName : "",
          employeeId: content.employeeId ? content.employeeId : "",
          employeeName: content.employeeName ? content.employeeName : "",
          projectId: content.projectId ? content.projectId : "",
          projectName: content.projectName ? content.projectName : "",
          assignBy: content.assignBy ? content.assignBy : "",
          assignName: content.assignName ? content.assignName : "",
          assignee: content.assignee ? content.assignee : null,
          receipt: content.receipt
            ? JSON.parse(JSON.stringify(content.receipt))
            : null,
          processors: content.processors
            ? JSON.parse(JSON.stringify(content.processors))
            : [],
          strProcessors: content.processors
            ? JSON.stringify(content.processors)
            : "",

          updatedOn: new Date(),
          updatedBy: this.userLogin.uid,
          updatedName: this.userLogin.displayName,
          author: this.userLogin,
          timestamp: new Date().getTime(),
          isReviewed: false,
        })
        // .catch((err) => {
        //   console.log("DialogWorkingTaskComponent/edit:", err);
        //   return err;
        // })
        .then((docRef) => {
          const syncData = { id: content.id, ...content };
          this.sync(syncData);

          return docRef;
        })
    );
  }

  changeStatus(shopId: string, id: string, status: number): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    let isFinished = status === 4 || status === 5 ? true : false;

    this.contentRef = this.db.collection(
      this.dbPath + `/${shopId}/working-tasks`
    );

    if (isFinished) {
      return this.contentRef.doc(id).update({
        status: status,
        isFinished: isFinished,
        finishedOn: new Date(),
        updatedOn: updatedOn,
        updatedBy: updatedBy,
        updatedName: this.userLogin.displayName,
        timestamp: new Date().getTime(),
      });
    } else {
      return this.contentRef.doc(id).update({
        status: status,
        isFinished: isFinished,
        updatedOn: updatedOn,
        updatedBy: updatedBy,
        updatedName: this.userLogin.displayName,
        timestamp: new Date().getTime(),
      });
    }
  }

  updateDescription(
    shopId: string,
    id: string,
    description: string
  ): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    this.contentRef = this.db.collection(
      this.dbPath + `/${shopId}/working-tasks`
    );
    return this.contentRef.doc(id).update({
      description: description,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      updatedName: this.userLogin.displayName,
      timestamp: new Date().getTime(),
    });
  }

  setActive(
    shopId: string,
    id: string,
    issueDate: Date,
    isActive: boolean
  ): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    issueDate = issueDate ? issueDate : new Date();

    this.contentRef = this.db.collection(
      this.dbPath + `/${shopId}/working-tasks`
    );
    return this.contentRef.doc(id).update({
      issueDate: issueDate,
      isActive: isActive,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      updatedName: this.userLogin.displayName,
      timestamp: new Date().getTime(),
    });
  }

  delete(shopId: string, id: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    this.contentRef = this.db.collection(
      this.dbPath + `/${shopId}/working-tasks`
    );
    return this.contentRef.doc(id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      updatedName: this.userLogin.displayName,
      timestamp: new Date().getTime(),
    });
  }

  sync(content) {
    content.createdOn ? delete content.createdOn : null;
    content.updatedOn ? delete content.updatedOn : null;
    content.content ? delete content.content : null;

    content.tags = JSON.stringify(content.tags);
    content.imagesUrl = JSON.stringify(content.imagesUrl);
    content.fsId = content.id ? content.id : "";

    if (environment.syncLive) {
      this.http.post(this.baseUrl + "/WorkingTask", content).subscribe({
        next: () => {},
        error: (err) => {},
      });
    }
  }
}
